'use client'
import React from 'react'
import { Director } from '@/cinematics/Director'
import { Layout } from '@/components/UI/Layout'
import { RedirectActor } from '@/actors/RedirectActor'
import { PlaylistActor } from '@/actors/PlaylistActor'
import { AuthActor } from '@/actors/AuthActor'
import { LAYOUT_TYPE } from '@/constants/auth'
import { IActor, ICinematics, IScene } from '@/types'
import { IScript } from '@/types/cinematics'
import { IRedirectScript } from '@/actors/RedirectActor/types'
import { IPlaylistActorScript } from '@/actors/PlaylistActor/types'

const ACTOR_VIEW_ID = {
  REDIRECT: 'RedirectActor',
  PLAYLIST: 'PlaylistActor',
  AUTH: 'AuthActor'
}

// What Director hands each actor: the matched actor, its script, the scene's
// `set` and the cinematic's `ambiance`.
interface IActorComponentProps extends IActor {
  script: Partial<IScript>
  index: number
  set?: IScene['set']
  ambiance?: ICinematics['ambiance']
}

// Spread the whole set through rather than picking `script` off: dropping `set`
// is what left AuthLayout blind to `set.navbar` / `set.videos` on catch-all
// scenes.
const ActorComponent = (props: IActorComponentProps): React.JSX.Element | null => {
  const { viewId } = props

  if (!viewId) return null

  return (
    <>
      {/* These two declare their own narrow script types and read nothing else
          off the actor, so their script is narrowed here rather than spread. */}
      {viewId === ACTOR_VIEW_ID.REDIRECT &&
        <RedirectActor script={props.script as IRedirectScript} />
      }
      {viewId === ACTOR_VIEW_ID.PLAYLIST &&
        <PlaylistActor script={props.script as IPlaylistActorScript} />
      }
      {viewId === ACTOR_VIEW_ID.AUTH &&
        // No structural form here, so `initialAuthForm` is deliberately omitted:
        // the scene's `options.defaultForm` chooses the landing form (falling
        // back to `main`), and `options.defaultLayout` overrides the layout.
        // A partner scene configures its own shape without a page change.
        <AuthActor
          {...props}
          initialLayoutType={LAYOUT_TYPE.SINGLE_COLUMN}
        />
      }
    </>
  )
}

const DefaultPage = (props: any) => {
  const { children } = props

  return (
    <section>
      <Layout hasMarginX={false} hasFooter={false}>
        {/* By reference, not an inline arrow: a new component type per render
            remounted the actor on every `?form=` change (PRO-783). */}
        <Director ActorComponents={ActorComponent}>
          {children}
        </Director>
      </Layout>
    </section>
  );
}

export default DefaultPage
